import React, { useEffect, useState } from 'react';
import { Formik, getIn } from 'formik';
import { Container, Row, Col, Form, Button, Card, Spinner, Alert } from 'react-bootstrap';
import * as Yup from 'yup';
import API from '../api/axios';
import { logout } from '../features/auth/authSlice';
import { useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { setLanguage } from '../features/language/languageSlice';
import normalizeLocale from '../utils/i18n-utils';
import StatusAlert from '../components/StatusAlert';
import { buildSuccessAlert, buildErrorAlert, buildWarningAlert, getApiErrorMessage, formatAlertMessage, } from '../utils/alert';

const DEFAULTS = {
  user: {
    firstName: '',
    lastName: '',
    email: '',
    defaultLocale: 'es',
  },
  password: {
    current: '',
    new: '',
    confirm: '',
  },
};


function Settings() {
  const [initialValues, setInitialValues] = useState(DEFAULTS);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [alert, setAlert] = useState(null);
  const [localeOptions, setLocaleOptions] = useState([]);
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { t } = useTranslation(['common', 'settings']);
  const lang = useSelector((state) => state.language.lang);

  const Schema = Yup.object({
    user: Yup.object({
      firstName: Yup.string().required(t('settings:validation.firstName')),
      lastName: Yup.string().required(t('settings:validation.lastName')),
      defaultLocale: Yup.string().required(t('settings:validation.displayLanguage')),
    }).required(),
    password: Yup.object({
      current: Yup.string().when('new', {
        is: (val) => !!val,
        then: (s) => s.required(t('settings:validation.currentPassword')),
        otherwise: (s) => s.optional(),
      }),
      new: Yup.string()
        .nullable()
        .test('password-strength', t('settings:validation.weakPassword'), (v) => {
          if (!v) return true; // 如果为空，则通过
          // 使用单个正则表达式验证所有条件
          const passwordRegex = /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d).{8,}$/;
          return passwordRegex.test(v);
        }),
      confirm: Yup.string().when('new', {
        is: (val) => !!val,
        then: (s) => s.required(t('settings:validation.confirmNewPassword'))
          .oneOf([Yup.ref('new')], t('settings:validation.passwordMismatch')),
        otherwise: (s) => s.optional(),
      }),
    }),
  });

  // 加载用户资料（GET /auth/me）
  useEffect(() => {
    (async () => {
      setLoading(true);
      setAlert(null);
      try {
        const res = await API.get('/auth/me');
        const { profile, localeOptions: opts } = res.data || {};
        console.log(res.data);
        const values = {
          user: {
            firstName: profile?.firstName || '',
            lastName: profile?.lastName || '',
            email: profile?.email || '',
            defaultLocale: profile?.defaultLocale || 'en',
          },
          password: { current: '', new: '', confirm: '' },
        };
        if (Array.isArray(opts) && opts.length > 0) {
          let finalOpts = opts;
          setLocaleOptions(finalOpts);
        }
        setInitialValues(values);
      } catch (e) {
        setAlert(buildErrorAlert(getApiErrorMessage(e, t('settings:info.loadProfileError'))));
        setInitialValues(DEFAULTS);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  // ⬇️ 新增：是否需要重登 + 倒计时秒数
  const [needRelogin, setNeedRelogin] = useState(false);
  const [redirectIn, setRedirectIn] = useState(5);

  async function logoutAndRedirect() {
    try {
      const resultAction = await dispatch(logout());
      if (logout.fulfilled.match(resultAction)) {
        // 登录成功后跳转到 Dashboard
        navigate('/login', { replace: true, state: { msg: t('settings:info.pleaseSignInAgain') } });
      } else {
        // 登录失败，显示错误信息
        /* setError(resultAction.payload || resultAction.error.message || 'Logout error network'); */
      }
    } catch (error) {
      /* setError('Logout error'); */
    }
  };

  // 提交映射（PATCH /auth/me）
  async function handleSubmit(values) {
    setSaving(true);
    setAlert(null);

    try {
      const payload = {
        firstName: values.user.firstName,
        lastName: values.user.lastName,
        defaultLocale: values.user.defaultLocale,
      };

      if (values.password.new) {
        payload.currentPassword = values.password.current;
        payload.newPassword = values.password.new;
        payload.confirmNewPassword = values.password.confirm;
      }

      const res = await API.patch('/auth/me', payload);

      if (res.data?.forceRelogin) {
        setNeedRelogin(true);
        setRedirectIn(5);
        setAlert(
          buildWarningAlert(t('settings:info.passwordChangeSuccessRelogin'))
        );
        return;
      }

      dispatch(setLanguage(normalizeLocale(values.user.defaultLocale)));

      setAlert(
        buildSuccessAlert(t('settings:info.profileUpdateSuccess'), {
          details: [
            `${t('settings:label.displayLanguage')}: ${values.user.defaultLocale}`,
          ],
        })
      );
    } catch (e) {
      setAlert(
        buildErrorAlert(
          getApiErrorMessage(e, t('settings:info.loadProfileError'))
        )
      );
    } finally {
      setSaving(false);
    }
  }

  useEffect(() => {
    if (!needRelogin) return;
    if (redirectIn <= 0) {
      logoutAndRedirect();
      return;
    }
    const id = setTimeout(() => {
      setRedirectIn((s) => s - 1);
    }, 1000);
    return () => clearTimeout(id);
  }, [needRelogin, redirectIn]);

  if (loading) {
    return (
      <div className="p-3 d-flex align-items-center">
        <Spinner animation="border" size="sm" className="me-2" />
        Loading…
      </div>
    );
  }

  return (


    <Formik
      enableReinitialize
      initialValues={initialValues}
      validationSchema={Schema}
      onSubmit={handleSubmit}
      validateOnChange={false}
      validateOnBlur={true}
    >
      {(formik) => {
        // 小工具：读取嵌套字段的 touched/error
        const invalid = (path) => {
          const t = getIn(formik.touched, path);
          const e = getIn(formik.errors, path);
          return [!!(t && e), e];
        };

        return (
          <Container fluid className="d-flex flex-column">
            <Row className="justify-content-center">
              <Col sm={12} className='mb-4'>
                <span className="fw-semibold">{t('settings:title.settings')}</span>
              </Col>
              <Form noValidate onSubmit={formik.handleSubmit}>
                {needRelogin && (
                  <Alert variant="warning" className="d-flex justify-content-between align-items-center">
                    <div>
                      {t('settings:info.passwordChangeSuccessRelogin')}
                      {t('settings:info.reloginCountdown', { seconds: redirectIn })}
                    </div>
                    <div>
                      <Button size="sm" variant="outline-primary" onClick={logoutAndRedirect}>
                        {t('settings:info.reloginNow')}
                      </Button>
                    </div>
                  </Alert>
                )}
                {!needRelogin && (
                  <StatusAlert
                    alert={alert}
                    onClose={() => setAlert(null)}
                  />
                )}
                <Card className="mb-3">
                  <Card.Body>
                    <h6 className="mb-3">{t('settings:title.personalInfo')}</h6>
                    <Row>
                      <Col md={6} className="mb-3">
                        <Form.Label className="form-label">{t('settings:label.firstName')} *</Form.Label>
                        <Form.Control
                          name="user.firstName"
                          value={formik.values.user.firstName || ''}
                          onChange={formik.handleChange}
                          onBlur={formik.handleBlur}
                          isInvalid={invalid('user.firstName')[0]}
                          placeholder={t('settings:label.firstName')}
                        />
                        <Form.Control.Feedback type="invalid">
                          {invalid('user.firstName')[1]}
                        </Form.Control.Feedback>
                      </Col>

                      <Col md={6} className="mb-3">
                        <Form.Label className="form-label">{t('settings:label.lastName')} *</Form.Label>
                        <Form.Control
                          name="user.lastName"
                          value={formik.values.user.lastName || ''}
                          onChange={formik.handleChange}
                          onBlur={formik.handleBlur}
                          isInvalid={invalid('user.lastName')[0]}
                          placeholder={t('settings:label.lastName')}
                        />
                        <Form.Control.Feedback type="invalid">
                          {invalid('user.lastName')[1]}
                        </Form.Control.Feedback>
                      </Col>
                    </Row>

                    <Row>
                      <Col md={6} className="mb-3">
                        <Form.Label className="form-label">{t('settings:label.email')} *</Form.Label>
                        <Form.Control
                          name="user.email"
                          type="email"
                          value={formik.values.user.email || ''}
                          onChange={formik.handleChange}
                          onBlur={formik.handleBlur}
                          disabled
                        />
                      </Col>

                      <Col md={6} className="mb-3">
                        <Form.Label className="form-label">{t('settings:label.displayLanguage')} *</Form.Label>
                        <Form.Select
                          name="user.defaultLocale"
                          value={formik.values.user.defaultLocale || ''}
                          onChange={formik.handleChange}
                          onBlur={formik.handleBlur}
                          isInvalid={invalid('user.defaultLocale')[0]}
                        >
                          {localeOptions.map((opt, idx) => (
                            <option key={opt.value || `loc-${idx}`} value={opt.value}>
                              {opt.label || opt.value}
                            </option>
                          ))}
                        </Form.Select>
                        <Form.Control.Feedback type="invalid">
                          {invalid('user.defaultLocale')[1]}
                        </Form.Control.Feedback>
                      </Col>
                    </Row>
                  </Card.Body>
                </Card>

                <Card className="mb-3">
                  <Card.Body>
                    <h6 className="mb-3">{t('settings:title.changePassword')}</h6>
                    <Row>
                      <Col md={4} className="mb-3">
                        <Form.Label className="form-label">{t('settings:label.currentPassword')}</Form.Label>
                        <Form.Control
                          name="password.current"
                          type="password"
                          value={formik.values.password.current || ''}
                          onChange={formik.handleChange}
                          onBlur={formik.handleBlur}
                          isInvalid={invalid('password.current')[0]}
                          placeholder={t('settings:placeholder.currentPassword')}
                        />
                        <Form.Control.Feedback type="invalid">
                          {invalid('password.current')[1]}
                        </Form.Control.Feedback>
                      </Col>

                      <Col md={4} className="mb-3">
                        <Form.Label className="form-label">{t('settings:label.newPassword')}</Form.Label>
                        <Form.Control
                          name="password.new"
                          type="password"
                          value={formik.values.password.new || ''}
                          onChange={formik.handleChange}
                          onBlur={formik.handleBlur}
                          isInvalid={invalid('password.new')[0]}
                          placeholder={t('settings:placeholder.newPassword')}
                        />
                        <Form.Control.Feedback type="invalid">
                          {invalid('password.new')[1]}
                        </Form.Control.Feedback>
                      </Col>

                      <Col md={4} className="mb-3">
                        <Form.Label className="form-label">{t('settings:label.confirmNewPassword')}</Form.Label>
                        <Form.Control
                          name="password.confirm"
                          type="password"
                          value={formik.values.password.confirm || ''}
                          onChange={formik.handleChange}
                          onBlur={formik.handleBlur}
                          isInvalid={invalid('password.confirm')[0]}
                          placeholder={t('settings:placeholder.confirmNewPassword')}
                        />
                        <Form.Control.Feedback type="invalid">
                          {invalid('password.confirm')[1]}
                        </Form.Control.Feedback>
                      </Col>
                    </Row>
                  </Card.Body>
                </Card>

                <div className="text-end">
                  <Button type="submit" disabled={saving} className="border ms-1 btn-custom-success">
                    {saving ? (<><Spinner animation="border" size="sm" className="me-2" />{t('common:buttons.saving')}</>) : t('common:buttons.save')}
                  </Button>
                </div>
              </Form>
            </Row>
          </Container>
        );
      }}
    </Formik>

  );
}

export default Settings;
